import type { CSSProperties } from "react";
import { motionCssVars } from "@/lib/motion-tokens";
import { MICRO_MOTION_CSS } from "@/lib/micro-motion-css";
import { BASE_CSS } from "./hp-base";
import { PRVIDEO_CSS } from "./sec-prvideo";
import { STORY_CSS } from "./sec-story";
import { ABOUT_VOICES_CSS, ROW_ARROWS_CSS, CLOSING_CSS, CLOSING_FLOW_CSS, DARK_FRAME_CSS, DT_CSS, GRANT_CSS, HL_CSS, PLANS_CSS } from "./sec-phase1";
import { PHASE2A_CSS } from "./sec-phase2a";
import { PHASE2B_CSS } from "./sec-phase2b";
import { FAQ_ENTRY_CSS, FQ_CSS } from "./sec-phase2c";
import { VS_CSS } from "./sec-vs";
import { RHYTHM_CSS } from "./sec-rhythm";
import { ENGINEER_CSS } from "./sec-engineer";
import { MROW_CSS } from "./sec-mrow";
import { MOTION_CSS } from "./motion";
import { PAPER_CSS } from "./paper-bg";
import { FV_FONT_FILES } from "./fv-fonts.generated";

/*
  /women のトップの見た目。組みと部品は開発演習の LP (lp/prod-dev) にそろえ、色は duo-b2 (水色と温かい黄) にする。
  2026-10-08 の指示。文言と節の並びは変えず、見た目だけをここで差し替える。

  色の役は次の通り。
  - 水色 #3EC1F3 は札、節の番号、見出し前の線、カードの上辺の線。白い地の上で字の色には使わない
  - 黄 #FFE566 は見出しの要の言葉のマーカーだけ。1つの節に1つまで
  - 節の地は白、淡い水色、区切りに濃い水色。最初の画面と締めの面だけ、水色から淡い黄への斜めのグラデ
  - ボタンは墨の地に白い字。LINE のボタンは LINE の緑のまま
  - 赤と青、影、紫から桃のグラデ、半透明のチップは使わない

  書体は app.css が body * に var(--font-body-ja) を !important で当てているので、その変数を .hp に置き直して差し替える。
  明朝と等幅は、使う所で変数を置き直すだけで済む (.hp-mi と .hp-mono)。

  このページと共有している部品 (CTAButton、Mark、CardRail、PhaseTimeline、InstructorEditorial、LineConsultCTA、
  FAQ の入口) は源を触らず、.hp の中だけで効く指定で色を合わせる。ほかのページの見た目は変わらない。
  動きの長さとイージングは motion-tokens の変数 (--m-*) だけを使う
*/
const LEGACY_CSS = `
.hp{--ink:#121417;--sub:#565D68;--sky:#3EC1F3;--sky-wash:#EEF8FD;--sky-d:#DDF2FC;--ymark:#FFE566;--rule:#D3E2EA;
  --gothic:"Noto Sans JP",var(--font-sans-ja);--mincho:"Noto Serif JP","Hiragino Mincho ProN","Yu Mincho",YuMincho,serif;--mono:"IBM Plex Mono",ui-monospace,SFMono-Regular,Menlo,monospace;
  --font-body-ja:var(--gothic);
  --sec:64px;--gap:40px;
  --grad:linear-gradient(135deg,rgba(62,193,243,.5) 0%,rgba(62,193,243,.2) 36%,rgba(255,244,194,.72) 76%,rgba(255,244,194,.95) 100%),linear-gradient(#FFFFFF,#FFFFFF);
  --color-shegate-accent:var(--sky);--color-shegate-accent-soft:var(--sky);--color-shegate-black:var(--ink);
  background:#fff;color:var(--ink);-webkit-font-smoothing:antialiased}
/* ヘッダーとフッターは .hp の外にあるので、html に同じゴシックを置く。トップの html.font-system-ja (OS の字) より後に読まれるのでこちらが勝つ。HP、受講生の声、料金のページだけ */
html:has(.hp){--font-body-ja:"Noto Sans JP",var(--font-sans-ja)}
html:has(.hp) .gh-tag,html:has(.hp) .gh-secrow a{font-weight:700}
.hp .hp-mi{--font-body-ja:var(--mincho)}
.hp .hp-mono{--font-body-ja:var(--mono)}
.hp .font-code,.hp .font-code *{font-family:var(--mono)!important}
.hp h1,.hp h2,.hp h3,.hp b,.hp strong,.hp blockquote{font-feature-settings:"palt";word-break:auto-phrase}
/* Safari は auto-phrase を知らず、親の keep-all を継いで句読点の無い太字が折れずに横へはみ出す。はみ出すときだけ折る */
.hp{overflow-wrap:break-word}

/* 節の地と上下の余白。余白は LP と同じくスマホ 96、PC 128 */
.hp .f-white{background:#fff}
.hp .f-wash{background:var(--sky-wash)}
.hp .f-sky{background:var(--sky-d)}
.hp .f-grad{background-color:#fff;background-image:var(--grad);background-size:cover;background-position:center;color:var(--ink)}

/* 見出し。h2 はスマホ 28〜32、PC 40〜52 (LP と同じ)。前に水色の短い線を置いた英字の札を添える */
.hp section h2{font-weight:var(--h2-w);font-size:clamp(28px,8vw,32px);line-height:1.45;letter-spacing:-.02em;color:var(--ink)}
/* 英字の札。共有の CardRail などが出す英字 (tracking 0.22em) も同じ形にする */
.hp p.tracking-\\[0\\.22em\\]{display:flex;align-items:center;gap:10px;--font-body-ja:var(--mono);font-size:12px;font-weight:600;letter-spacing:.16em;color:var(--ink);text-transform:uppercase}
.hp p.tracking-\\[0\\.22em\\]::before{content:"";width:26px;height:4px;background:var(--sky);flex:none}
.hp p.tracking-\\[0\\.22em\\]+h2{margin-top:16px}

/* ボタン。共有の CTAButton の主導線は墨の地に白い字にする */
.hp .bg-shegate-lime{background-color:var(--ink);color:#fff;border-radius:8px;transition:background-color var(--m-cta) var(--m-out)}
.hp .bg-shegate-lime:hover{background-color:#000;filter:none}
.hp .border-shegate-lime-ink{border-color:var(--ink);color:var(--ink);background-color:#fff;border-radius:8px}
.hp .border-shegate-lime-ink:hover{background-color:var(--ink);color:#fff}
.hp a:focus-visible,.hp button:focus-visible{outline:2px solid var(--focus-ring);outline-offset:2px}

/* 差し色の朱を当てていた字は墨に戻す。水色は白い地の字には使わない */
.hp [class*="text-shegate-accent"]{color:var(--ink)}
.hp .text-shegate-black,.hp .text-\\[\\#252525\\],.hp .text-\\[\\#1a1a1a\\]{color:var(--ink)}
.hp button.text-shegate-black:hover:not(:disabled){color:#fff}
.hp button.text-shegate-black:disabled,.hp button.text-shegate-black:disabled:hover{color:#B8C0C8}
.hp .text-\\[\\#899AA4\\],.hp .text-\\[\\#8fa5b6\\],.hp .text-\\[\\#5b5b5b\\],.hp .text-\\[\\#6b6b6b\\],.hp .text-\\[\\#5b6b76\\]{color:var(--sub)}
/* 水色の地に乗る字は墨 */
.hp .bg-shegate-accent.text-white,.hp .bg-shegate-accent .text-white{color:var(--ink)}
/* 本文の強調 (Mark) は太字だけにする。黄は見出しの要の言葉に残す */
.hp span[style*="--color-shegate-accent"]{background-image:none!important;font-weight:700}

/* 暖色の罫と地を、水色寄りの淡い色に置き換える */
.hp .border-\\[\\#e0ddd6\\],.hp .border-\\[\\#ebe8e2\\],.hp .border-\\[\\#e6e3dd\\],.hp .border-\\[\\#cfcabd\\],.hp .border-gray-200{border-color:var(--rule)}
.hp .hover\\:border-\\[\\#cfcabd\\]:hover{border-color:var(--sky)}
.hp .bg-\\[\\#e0ddd6\\],.hp .bg-\\[\\#ebe8e2\\],.hp .bg-gray-200{background-color:var(--rule)}
.hp .bg-\\[\\#f6f4ef\\],.hp .bg-\\[\\#f3f1ec\\],.hp .bg-\\[\\#faf9f6\\],.hp .bg-\\[\\#f1eee7\\],.hp .bg-\\[\\#eef1f3\\]{background-color:var(--sky-wash)}
.hp .bg-\\[\\#dedad2\\]{background-color:#E3E8EC}

/* 768 の2列で、keep-all の長い句が列より広がって横にはみ出すのを防ぐ。収まるときは今までどおり折り返さない */
.hp .break-keep{overflow-wrap:anywhere}

/* 角丸は LP にそろえて小さく。影は使わない */
.hp .rounded-2xl,.hp .rounded-3xl{border-radius:8px}
.hp .rounded-xl{border-radius:6px}
.hp .rounded-t-2xl{border-radius:8px 8px 0 0}
.hp .rounded-b-2xl{border-radius:0 0 8px 8px}
.hp [class*="shadow"]{box-shadow:none!important}

`;

// 最初の画面 (ホームだけ)
const FV_CSS = `/* 最初の画面。ブランド広告 (brand-ad) の見た目。墨の地に白い字、明朝、白の札の CTA。黄は見出しのマーカーだけ。
   背景は人の動画 (hp-fv-bg)。字は左、数の帯は下に 1 行 (PC) */
.hp .hp-fv{--fv-ink:#0E0E10;--fv-dim:rgba(255,255,255,.72);--fv-pad:20px;position:relative;display:grid;grid-template-columns:minmax(0,1fr);background:var(--fv-ink);color:#fff;letter-spacing:.04em;word-break:keep-all;overflow:hidden}
.hp .hp-fv-copy{grid-row:1;position:relative;z-index:2;padding:6px var(--fv-pad) 0}
.hp .hp-fv-foot{grid-row:2;position:relative;z-index:2;padding:22px var(--fv-pad) 44px}
/* 見出しの上の札と 2 行。スマホは札を左に置き、右に 2 行を積んで 1 段にする (label は display:contents で札と字を格子に出す)。PC は札と 1 行目が並び、2 行目が下 */
.hp .hp-fv-tag{display:grid;grid-template-columns:auto minmax(0,1fr);column-gap:10px;align-items:center}
.hp .hp-fv-label{display:contents;margin:0;white-space:nowrap;font-size:12px;font-weight:700;line-height:1.45;letter-spacing:.02em;color:#fff}
.hp .hp-fv-label span{grid-row:1/3;border:1px solid rgba(255,255,255,.5);border-radius:999px;padding:3px 10px;color:#fff;line-height:1.3}
.hp .hp-fv-sub{grid-column:2;margin:0;white-space:nowrap;font-size:clamp(12px,3.4vw,13px);font-weight:700;line-height:1.45;color:#fff}
.hp .hp-fv-h{margin:8px 0 0;font-weight:700;font-size:clamp(28px,8vw,40px);line-height:1.32;letter-spacing:.04em;color:#fff;font-feature-settings:normal;word-break:keep-all}
.hp .hp-fv-q{font-weight:700;font-size:.86em}
.hp .hp-fv-l{display:block}
.hp .hp-fv-l.hp-fv-q{line-height:calc(1.32em / .86)}
.hp .hp-fv-key{display:inline-block;margin-top:4px;padding:0 .12em;background:var(--ymark);color:var(--fv-ink)}
.hp .hp-fv-nw{white-space:nowrap}
/* 見出しは 3 行のまま折らない。字の欄の幅 (100cqi) に、いちばん長い2行目「プログラミングだけでは、」(12 字とあきで約 12.5 字ぶん) が
   収まる大きさまで、行ごとに下げる。収まる幅では上の決まりの大きさ (1em と .86em) のまま。1024 で2行目が折れていた */
@supports (width:1cqi){
  .hp .hp-fv-h{container-type:inline-size}
  .hp .hp-fv-h .hp-fv-l{white-space:nowrap;font-size:min(1em,calc(100cqi / 10.2))}
  .hp .hp-fv-h .hp-fv-l.hp-fv-q{font-size:min(.86em,calc(100cqi / 12.8))}
}
.hp .hp-fv-price{--font-body-ja:var(--gothic);margin:6px 0 0;font-size:13px;font-weight:700;line-height:1.5;letter-spacing:.02em;color:rgba(255,255,255,.8)}
.hp .hp-fv-lede{margin:8px 0 0;font-size:clamp(13px,3.6vw,14px);font-weight:700;line-height:1.55;color:rgba(255,255,255,.9)}
.hp .hp-fv-cta{display:flex;flex-wrap:wrap;align-items:center;gap:6px 22px;margin-top:12px}
/* 最初の画面のかめ (HeroEditorial の hp-fv-walk)。動くのはかめの上下 3px だけ。transform のみ。動きを減らす設定では止める */
.hp .hp-fv-walk{display:inline-flex;align-items:flex-end;gap:2px;align-self:flex-end;pointer-events:none}
.hp .hp-fv-road{display:flex;gap:10px;margin:0 0 7px}
.hp .hp-fv-road i{display:block;width:6px;height:6px;border-radius:50%;background:rgba(5,180,91,.5)}
.hp .hp-fv-road i:nth-child(-n+11){display:none}
.hp .hp-fv-kame{display:block;width:56px;height:auto;transform-origin:50% 100%;animation:hp-fv-bob 1.6s ease-in-out infinite alternate}
@keyframes hp-fv-bob{from{transform:translateY(0)}to{transform:translateY(-3px)}}
@media (prefers-reduced-motion:reduce){.hp .hp-fv-kame{animation:none}}
@media (min-width:1024px){
  .hp .hp-fv-cta{position:relative}
  .hp .hp-fv-walk{position:absolute;left:4px;top:calc(100% + 14px)}
  .hp .hp-fv-road i:nth-child(-n+11){display:block}
  .hp .hp-fv-kame{width:76px}
}
.hp .hp-fv-btn{--font-body-ja:var(--gothic);display:inline-flex;align-items:center;gap:14px;min-height:52px;padding:0 26px;border-radius:16px;background:#fff;color:var(--fv-ink);font-weight:700;font-size:17px;letter-spacing:.04em;white-space:nowrap;text-decoration:none;transition:background-color var(--m-cta) var(--m-out)}
.hp .hp-fv-btn:hover{background:#E6E6E2}
.hp .hp-fv-arrow{width:30px;height:18px;flex:none;transition:transform var(--m-cta) var(--m-out)}
.hp .hp-fv-btn:hover .hp-fv-arrow{transform:translateX(var(--m-arrow-x))}
.hp .hp-fv-link{--font-body-ja:var(--gothic);display:inline-flex;align-items:center;min-height:44px;font-weight:700;font-size:14px;color:#fff;text-decoration:underline;text-underline-offset:5px;text-decoration-color:rgba(255,255,255,.55);transition:text-decoration-color var(--m-cta) var(--m-out)}
.hp .hp-fv-link:hover{text-decoration-color:#fff}
.hp .hp-fv-btn:focus-visible,.hp .hp-fv-link:focus-visible{outline:2px solid var(--focus-ring);outline-offset:2px}

.hp .hp-fv-facts{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));row-gap:18px;border-top:1px solid rgba(255,255,255,.22);border-bottom:1px solid rgba(255,255,255,.22);padding:18px 0}
.hp .hp-fv-fact{min-width:0;padding:0 10px}
.hp .hp-fv-fact:nth-child(2n+1){padding-left:0}
.hp .hp-fv-fact:not(:nth-child(2n+1)){border-left:1px solid rgba(255,255,255,.22)}
.hp .hp-fv-num{margin:0;line-height:1;white-space:nowrap}
.hp .hp-fv-num b{font-size:24px;font-weight:700;font-variant-numeric:tabular-nums;letter-spacing:0;color:#fff}
.hp .hp-fv-num span{margin-left:2px;font-size:12px;font-weight:700;color:#fff}
.hp .hp-fv-ic{--icon-accent:rgba(255,229,102,.4);--icon-paper:#111;display:inline-block;vertical-align:middle;margin:-6px 6px -6px 0;color:rgba(255,255,255,.88)}
.hp .hp-fv-cap strong{font-weight:700;color:#fff}
.hp .hp-fv-cap{--font-body-ja:var(--gothic);white-space:pre-line;margin:6px 0 0;font-size:12px;line-height:1.5;letter-spacing:.02em;color:var(--fv-dim)}
.hp .hp-fv-note{--font-body-ja:var(--gothic);margin:8px 0 0;font-size:12px;line-height:1.5;color:var(--fv-dim)}
.hp .hp-fv-sign{display:flex;align-items:center;gap:18px;margin:26px 0 0}
.hp .hp-fv-sign span{font-size:18px;font-weight:700;letter-spacing:.08em;color:#fff}
.hp .hp-fv-sign img{height:18px;width:auto;filter:brightness(0) invert(1)}

/* 最初の画面の動きは「取り消し線」の1つだけ (2026-10-12、オーナーが案 B を選んだ)。
   字は最初の描画から見えている (opacity で出さないので LCP を遅らせない)。動くのは transform と色だけで、字の箱の大きさは変えない (CLS 0)。
   「プログラミングだけでは、」に白い線が左から走ってその行が少し沈み、続けて「会社に選ばれない。」が一段大きくなる。
   そのあと無料体験入学のボタンの矢印が一度だけ前へ押し出される (案 C。矢印の要素だけを動かすのでボタンの字に依らない。ボタンは動き中も押せる)。
   動きの CSS は LP と共通の部品 (lib/micro-motion-css.ts の mm-strike と mm-key と mm-push)。HeroEditorial が class を付ける。
   順番は線 300〜800ms、沈み 700〜1000ms、強調 800〜1250ms、矢印 1300〜1950ms で、合わせて 2 秒以内。同時に 2 つは動かさない。1回だけ。
   線は ::after の飾りなので読み上げには出ない。reduced motion では最初から線が引かれ、沈んで大きくなった形で止まる。
   常に動くのは背景の人の動画だけ。動画は fv-bg-video.ts が load の後に足す */

@media (min-width:768px){
  .hp .hp-fv{--fv-pad:40px;grid-template-columns:minmax(0,1fr) minmax(0,1fr);align-items:center}
  .hp .hp-fv-copy{grid-row:1;grid-column:1;padding:48px 24px 0 var(--fv-pad)}
  .hp .hp-fv-foot{grid-row:2;grid-column:1/-1;padding:28px var(--fv-pad) 32px}
  .hp .hp-fv-tag{display:block}
  .hp .hp-fv-label{display:flex;flex-wrap:nowrap;align-items:center;gap:8px;font-size:12px;letter-spacing:.06em}
  .hp .hp-fv-sub{margin:10px 0 0;font-size:15px;line-height:1.5}
  .hp .hp-fv-h{margin-top:6px;line-height:1.4}
  .hp .hp-fv-l.hp-fv-q{line-height:calc(1.4em / .86)}
  .hp .hp-fv-lede{margin-top:12px;line-height:1.8}
  .hp .hp-fv-price{margin-top:8px}
  .hp .hp-fv-lede{font-size:15px}
  .hp .hp-fv-facts{grid-template-columns:repeat(3,minmax(0,1fr))}
  .hp .hp-fv-fact:nth-child(2n+1){padding-left:10px}
  .hp .hp-fv-fact:not(:nth-child(2n+1)){border-left:0}
  .hp .hp-fv-fact:nth-child(3n+1){padding-left:0}
  .hp .hp-fv-fact:not(:nth-child(3n+1)){border-left:1px solid rgba(255,255,255,.22)}
  .hp .hp-fv-num b{font-size:28px}
  .hp .hp-fv-num span{font-size:12px}
  .hp .hp-fv-cap{font-size:12px}
  .hp .hp-fv-btn{min-height:60px;font-size:18px}
  .hp .hp-fv-cta{margin-top:20px}
  /* 字の欄は幅の半分 (1024 からは 42%) から左右の余白を引いた分。見出しの2行目「プログラミングだけでは、」
     (字の幅とあきで約 10.8 字ぶん。字の端の余りを足して 11.4 で割る) が欄に収まる大きさにする。.hp-fv を挟んで下の (min-width:900px) より強くする */
  .hp .hp-fv .hp-fv-h{font-size:clamp(26px,calc((50vw - var(--fv-pad) - 24px) / 11.4),40px)}
}
@media (min-width:1024px){
  .hp .hp-fv{grid-template-columns:minmax(0,42fr) minmax(0,58fr)}
  .hp .hp-fv .hp-fv-h{font-size:clamp(34px,calc((42vw - var(--fv-pad) - 24px) / 11.4),50px);line-height:1.35}
  .hp .hp-fv .hp-fv-l.hp-fv-q{line-height:calc(1.35em / .86)}
  /* 数の帯は 6 つを 1 行に。罫は列の境だけ */
  .hp .hp-fv-facts{grid-template-columns:repeat(6,minmax(0,1fr));padding:16px 0}
  .hp .hp-fv-fact:nth-child(n){padding-left:14px;border-left:1px solid rgba(255,255,255,.22)}
  .hp .hp-fv-fact:first-child{padding-left:0;border-left:0}
  .hp .hp-fv-foot{display:flex;flex-wrap:wrap;align-items:center;justify-content:space-between;column-gap:24px}
  .hp .hp-fv-facts{flex:1 0 100%}
  /* 右下に浮く「ご質問はこちら」の入口 (右 20px、幅およそ 120px) の左に収める。入口は動かさない。注の一文と並べて入らない幅 (1280 など) は、署名だけ次の行の右へ送る */
  .hp .hp-fv-sign{margin:10px max(0px,calc(156px - var(--fv-pad))) 0 auto}
  .hp .hp-fv-sign span{font-size:16px}
  .hp .hp-fv-sign img{height:16px}
}
@media (min-width:1200px){
  .hp .hp-fv{--fv-pad:max(40px,calc((100vw - 1280px) / 2 + 40px))}
  /* 左の余白は画面の幅とともに増える。42% の欄のままだと字の欄が細り (1920 で 422px、2560 で 371px)、見出しが小さくなっていた。
     字の欄を 560px (右のあき 24px を引いて 536px) に固定し、見出しはそこに2行目が収まる 47px にする */
  .hp .hp-fv{grid-template-columns:calc(var(--fv-pad) + 560px) minmax(0,1fr)}
  .hp .hp-fv .hp-fv-h{font-size:47px}
}
/* 768〜1023 は字の欄が狭く、数の帯を3列にすると注記「（基本エンジニアプラン以上）」が欄に入らなかった。
   最初の画面は keep-all なので、注記は語の途中で折れない。この幅だけ数の帯をスマホと同じ2列に戻す */
@media (min-width:768px) and (max-width:1023px){
  .hp .hp-fv .hp-fv-facts{grid-template-columns:repeat(2,minmax(0,1fr))}
  .hp .hp-fv .hp-fv-fact{padding-left:10px;border-left:0}
  .hp .hp-fv .hp-fv-fact:nth-child(2n+1){padding-left:0}
  .hp .hp-fv .hp-fv-fact:not(:nth-child(2n+1)){border-left:1px solid rgba(255,255,255,.22)}
}
/* 背景の人の動画 (hp-fv-v)。色を直した人の動画を背景いっぱいに敷き、字の下を墨のグラデーションで暗くする。
   スマホは上の帯にスマホの版 (480x400) を敷き、下へ向けて墨に溶かす。字はその下半分に載るので、帯の高さは CTA の下端が 620px 以下に収まる分だけ。
   PC は左 3 割を墨の地のまま空けて、動画の左の端を溶かし、人が字の欄の右に来るようにする。
   ポスターと動画は同じ箱に重ね、動画は流れ始めてから (data-playing) 出す */
.hp .hp-fv-bg{position:absolute;z-index:0;left:0;right:0;top:0;aspect-ratio:480/400;overflow:hidden;pointer-events:none}
.hp .hp-fv-bg picture{display:contents}
.hp .hp-fv-bgimg{position:absolute;inset:0;width:100%;max-width:none;height:100%;object-fit:cover;object-position:50% 0}
.hp .hp-fv-bg video{opacity:0}
.hp .hp-fv-bg video[data-playing]{opacity:1}
.hp .hp-fv-bg::after{content:"";position:absolute;inset:0;background:linear-gradient(90deg,rgba(14,14,16,.5),rgba(14,14,16,0) 60%),linear-gradient(180deg,rgba(14,14,16,0) 40%,rgba(14,14,16,.72) 66%,var(--fv-ink) 98%)}
.hp .hp-fv-v .hp-fv-copy{padding-top:158px}
@media (prefers-reduced-motion:no-preference){
  .hp .hp-fv-bg video{transition:opacity var(--m-fade) linear}
}
@media (min-width:768px){
  .hp .hp-fv-v{--fv-bg-l:40%}
  .hp .hp-fv-bg{aspect-ratio:auto;bottom:0}
  .hp .hp-fv-bgimg{left:var(--fv-bg-l);width:calc(100% - var(--fv-bg-l));object-position:20% 30%;-webkit-mask-image:linear-gradient(90deg,transparent,#000 30%);mask-image:linear-gradient(90deg,transparent,#000 30%)}
  .hp .hp-fv-bg::after{background:linear-gradient(90deg,var(--fv-ink) 0,var(--fv-ink) calc(var(--fv-bg-l) - 4%),rgba(14,14,16,.45) calc(var(--fv-bg-l) + 14%),rgba(14,14,16,0) calc(var(--fv-bg-l) + 30%)),linear-gradient(0deg,var(--fv-ink) 0,rgba(14,14,16,.8) 22%,rgba(14,14,16,0) 46%)}
  .hp .hp-fv-v .hp-fv-copy{padding-top:64px;padding-bottom:40px}
}
@media (min-width:1024px){
  .hp .hp-fv-v{--fv-bg-l:30%}
  .hp .hp-fv-v .hp-fv-copy{padding-top:88px;padding-bottom:64px}
}
@media (min-width:1200px){
}

`;

// ホームに置く共有の部品 (カリキュラム、FAQ の入口、認定講師、LINE の相談、下に貼り付く帯) の色合わせ
const PARTS_SKIN_CSS = `/* カリキュラムの横流しと、その下の章の一覧は1つの節。間を詰める */
.hp #curriculum{padding-bottom:var(--gap)}
/* カードの上に出る「この期間に進む章」は、半透明をやめて墨の面にする */
@media (min-width:768px){.hp .md\\:bg-\\[\\#1a1a1a\\]\\/92{background-color:var(--ink)}}
/* 共有のデータが持つ灰青の札は、白い字が読める濃さに寄せる */
.hp .bg-\\[\\#5b6b76\\]{background-color:#4A5562}

/* 「この期間に進む章」「受講生の声」の墨の面の中だけ、札の字を水色にする */
.hp .md\\:bg-\\[\\#1a1a1a\\]\\/92 [class*="text-shegate-accent"]{color:var(--sky)}

/* FAQ の入口 (共有の部品)。最初の画面の下は白、ページの中ほどは淡い水色。ボタンは白地に墨の枠 */
.hp section[aria-label="FAQのご案内"]{background:#fff;border-color:var(--rule)}
.hp section[aria-label="FAQのご案内"].bg-\\[\\#1a1a1a\\]{background:var(--sky-wash);padding-top:var(--gap);padding-bottom:var(--gap)}
.hp section[aria-label="FAQのご案内"] .text-white{color:var(--ink)}
.hp section[aria-label="FAQのご案内"] .text-white\\/65{color:var(--sub)}
.hp #women-faq-trigger-hero,.hp #women-faq-trigger-mid-page{background:#fff;color:var(--ink);border:1.5px solid var(--ink);transition:background-color var(--m-cta) var(--m-out),transform var(--m-cta) var(--m-out)}
.hp #women-faq-trigger-hero:hover,.hp #women-faq-trigger-mid-page:hover{background:var(--ymark)}

/* 認定講師 (共有の部品)。審査基準の黒い帯を白い地に戻し、字を墨にする */
.hp .hp-ins>section{padding-top:var(--sec);padding-bottom:var(--gap)}
.hp .hp-ins h2 .text-shegate-accent{background:linear-gradient(transparent 58%,var(--ymark) 58%)}
.hp .hp-ins .rounded-\\[20px\\]{border-radius:6px}
.hp .hp-ins>.bg-\\[\\#1a1a1a\\]{background:#fff;padding-top:0;padding-bottom:var(--sec)}
.hp .hp-ins>.bg-\\[\\#1a1a1a\\]>div{border-top:1px solid var(--ink);padding-top:var(--gap)}
.hp .hp-ins>.bg-\\[\\#1a1a1a\\] :is(.text-white,.text-white\\/90){color:var(--ink)}
.hp .hp-ins>.bg-\\[\\#1a1a1a\\] :is(.text-white\\/60,.text-white\\/50){color:var(--sub)}
.hp .hp-ins>.bg-\\[\\#1a1a1a\\] :is(.md\\:border-white\\/20,.border-white\\/10){border-color:var(--rule)}
.hp .hp-ins>.bg-\\[\\#1a1a1a\\] p.tracking-wide{--font-body-ja:var(--mono);letter-spacing:.12em}


`;

const STICKY_BAR_CSS = `/* 画面の下に貼り付く無料30日チャレンジの帯 (共有の部品)。墨の帯にすると墨のボタンが沈むので、白い帯に墨の細い枠と字 */
.hp .fixed.inset-x-0.bottom-0>.bg-\\[\\#1a1a1a\\]{background:#fff;border:1px solid var(--ink);border-radius:8px}
.hp .fixed.inset-x-0.bottom-0>.bg-\\[\\#1a1a1a\\] .text-white{color:var(--ink)}
.hp .fixed.inset-x-0.bottom-0>.bg-\\[\\#1a1a1a\\] .text-white\\/60{color:var(--sub)}

`;

const LEGACY_PC_CSS = `@media (min-width:900px){
  .hp{--sec:88px;--gap:48px;--m-enter-y:var(--m-enter-y-pc)}
}
`;

/* 旧い見た目 (水色の組み) の上に、新しい土台 (hp-base.ts) と第1段の節 (sec-phase1.ts) を重ねる。
   第2段の節は自分の CSS を sec-<名前>.ts に置いて、ここに足す */
/* ホーム (.hp-home) の最初の画面の字だけ自前で配る (scripts/build-women-fv-fonts.ts が切り出す)。
   Google Fonts の CSS は読み込みの後に足すので、それが届くまでの間も、
   最初の画面の字は最初から Noto Sans JP と Noto Serif JP で出る。
   family を別名にしているのは、あとから届く Google の同名の指定と取り合わないため。
   字が無いものは次の family (Google の Noto、無ければ端末の字) へ 1 字ずつ落ちる。
   料金、受講生の声も .hp を使うが、そちらは先読みもしないので、ここは足さずホームだけに効かせる。
   .hp.hp-home は .hp より詳細度が高く、html:has(.hp-home) は html:has(.hp) と同じで後ろに置くため、順番に依らず勝つ */
export const HP_HOME_FONT_CSS = `
@font-face{font-family:"Women FV Sans";font-style:normal;font-weight:400;font-display:swap;src:url("${FV_FONT_FILES.sans400}") format("woff2")}
@font-face{font-family:"Women FV Sans";font-style:normal;font-weight:700;font-display:swap;src:url("${FV_FONT_FILES.sans700}") format("woff2")}
@font-face{font-family:"Women FV Sans";font-style:normal;font-weight:900;font-display:swap;src:url("${FV_FONT_FILES.sans900}") format("woff2")}
@font-face{font-family:"Women FV Serif";font-style:normal;font-weight:700;font-display:swap;src:url("${FV_FONT_FILES.serif700}") format("woff2")}
.hp.hp-home{--gothic:"Women FV Sans","Noto Sans JP",var(--font-sans-ja);--mincho:"Women FV Serif","Noto Serif JP","Hiragino Mincho ProN","Yu Mincho",YuMincho,serif;--font-body-ja:var(--gothic)}
html:has(.hp-home){--font-body-ja:"Women FV Sans","Noto Sans JP",var(--font-sans-ja)}
`;

/* ホームだけを静かな冊子の見た目にする (2026-10-13 オーナーが前と後の比較を見て決めた)。
   地は白・生成り・黒の3つ。黄と水色のマーカーは真鍮色の細い下線1本。見出しは字の間を広げ、スマホも PC と同じ 700。角は 2px にして影を消す。
   札のリボンと節の斜めの切り口、LINE の緑のボタンの丸みは残す。料金、受講生の声、30日チャレンジも .hp の部品を使うので、.hp.hp-home の中だけに効かせる。
   詳細度が .hp .x より1つ高いので、節の中に置いた <style> より後ろでなくても勝つ */
export const HP_HOME_LUXURY_CSS = `
.hp.hp-home{--brass:#A8895A;--brass-l:#C9AE7C;--kinari:#F7F3EA;--h2-w:700}
.hp.hp-home .hx-blue{background:var(--black)}
.hp.hp-home .hx-blue .hx-b-em,.hp.hp-home .hx-blue .hx-head .px-em{color:var(--brass-l)}
.hp.hp-home .hx-yellow{background:var(--kinari);--sub:#565D68}
.hp.hp-home .hx-wash{background:var(--kinari)}
.hp.hp-home .hx-cream{background-color:var(--kinari)}
.hp.hp-home .hx-about-head::before{background:var(--kinari)}
.hp.hp-home :is(.hx-hl,.px-em,.em mark,.hx-qi strong,.hx-vc-full strong,.vd-a mark,.pp-vs-mk),.hp.hp-home .pp-pt td.yes .pp-pt-pr{background:linear-gradient(var(--brass),var(--brass)) 0 100%/100% 1px no-repeat;padding-bottom:.06em}
.hp.hp-home :is(.hx-dark,.hx-blue) :is(.hx-hl,.px-em){background-image:linear-gradient(var(--brass-l),var(--brass-l))}
.hp.hp-home .hx-blue .hx-head .px-em{background:none}
.hp.hp-home .hx-bands>span{background:linear-gradient(var(--brass),var(--brass)) 50% 100%/100% 1px no-repeat;color:inherit;padding:2px .2em 6px}
/* 動きのある版は帯が左から伸びる (motion.ts)。帯を下線1本にして、線が左から引かれる動きにする */
html.hx-motion .hp.hp-home .hx-head-c[data-mo] .hx-bands>span::before{top:auto;height:1px;background:var(--brass)}
.hp.hp-home :is(.hx-h2,.hx-h2.hx-bands,.hx-h2.hx-strike){letter-spacing:.06em}
.hp.hp-home :is(
  .em-ng,.em-no,.em-card,.em-ic,.em-chip,.em-drop-track,.em-drop-track>i,.em-k,.em-down,.em-dv-n,.em-it,
  .hx-frame,.hx-pd-tab,.hx-pd-stack>span,.hx-pd-shot,.px-pc,.px-pc-ico,.px-chips>span,.px-grp,.px-grp-tag,.px-rt,.px-app,
  .hx-pill,.hx-pdt-plate,.hx-i-photo,.hx-i-role,.hx-i-topics>span,.hx-i-crit-l>li,.hx-arr,.hx-vc,.hx-vc-pm,.hx-vf-box,.hx-vf-box button,
  .hx-wyg-card,.hx-wyg-ic,.hx-wyg-cat,.hx-wyg-list,.hx-wyg-boxin,.hx-wyg-meter>span,.hx-pb-nm em,
  .pp-pt-frame,.pp-pt-flag,.pp-pay-card,.pp-sim,.pp-sim-seg,.pp-sim-plan,.pp-sim-seg>button,.pp-sim-step>button,.pp-sim-val,.pp-sim-chip,.pp-pay-note,.pp-vs-frame,.pp-vs-flag,
  .hx-grant,.hx-grant-flow>li,.hx-end-step,.hx-end-no,.hx-end-opt
){border-radius:2px}
.hp.hp-home .hx-pb-a{border-radius:2px 0 0 2px}
.hp.hp-home .hx-pb-a:last-child{border-radius:2px}
.hp.hp-home .hx-pb-b{border-radius:0 2px 2px 0}
.hp.hp-home .hx-pb-open{border-radius:0}
.hp.hp-home .hx-end-pic{border-radius:2px 2px 0 0}
.hp.hp-home .hx-end-step:nth-child(5) .hx-end-pic{border-radius:2px 0 0 2px}
@media (min-width:900px){.hp.hp-home .hx-end-step:nth-child(5) .hx-end-pic{border-radius:2px 2px 0 0}}
.hp.hp-home .em-card{box-shadow:none}
/* 学ぶ中身の節の終わりは写真の帯。下の余白を 0 にして、次の黒い節の斜めの切り口が写真に直接かかるようにする。
   余白があると、写真と切り口の間に生成りの細い三角がのぞく */
.hp.hp-home #curriculum{padding-bottom:0}

/* ゆめさくエンジニアとは。本文は字と行の間を広げる。ほかのスクールの型は細い線1本、ゆめさくの18ヶ月は黒い帯1本に金の罫 (2026-10-13 オーナーが B を選んだ) */
.hp.hp-home .hx-about-lede{font-size:16px;line-height:2.3;letter-spacing:.06em}
.hp.hp-home .hx-about-lede b{font-weight:700;background:linear-gradient(var(--brass),var(--brass)) 0 100%/100% 1px no-repeat;padding-bottom:2px}
.hp.hp-home .hx-scope{margin-top:64px}
.hp.hp-home .hx-scope-lab{font-weight:400;letter-spacing:.04em}
.hp.hp-home .hx-scope-lab-us{color:var(--ink);font-weight:700}
.hp.hp-home .hx-scope-short{border:0;border-top:1px solid #B9B1A2;background:none;justify-content:flex-start;align-items:flex-start;min-height:0;padding:8px 0 0}
.hp.hp-home .hx-scope-short.hx-scope-flat{border-top-style:dotted;padding:8px 0 0}
.hp.hp-home .hx-scope-short span{font-weight:400;letter-spacing:.04em}
.hp.hp-home .hx-scope-other+.hx-scope-other{margin-top:18px}
.hp.hp-home .hx-scope-side b{font-weight:700;letter-spacing:.04em}
.hp.hp-home .hx-scope-gap{margin-top:36px}
.hp.hp-home .hx-scope-tr{border:0;background:var(--black)}
.hp.hp-home .hx-scope :is(.hx-lv-b,.hx-lv-a,.hx-lv-p){background:var(--black);color:#fff}
.hp.hp-home .hx-scope-tr>span{border-right:1px solid var(--brass)}
.hp.hp-home .hx-scope-tr>span.hx-scope-in{border-right-color:rgba(255,255,255,.14)}
.hp.hp-home .hx-scope-tr>span:last-child{border-right:0}
.hp.hp-home .hx-scope-tags i{border:0;background:none;color:rgba(255,255,255,.72);padding:0;font-weight:400}
.hp.hp-home .hx-scope-cards li{border:0;border-left:1px solid var(--brass)}
.hp.hp-home .hx-scope-ticks span{border-left-color:#DED7C9;--font-body-ja:var(--mono);font-family:var(--mono);font-size:clamp(7px,calc(100cqi / 18 * .7),11px)}
.hp.hp-home .hx-scope-ticks span.hx-scope-tick-s{border-left-color:var(--brass)}
.hp.hp-home .hx-scope-ticks span:last-child{border-right-color:var(--brass)}
.hp.hp-home .hx-scope-cap-us b{--font-body-ja:var(--mincho);font-family:var(--mincho);font-size:15px;letter-spacing:.06em;margin-bottom:2px}

/* 料金の節は長いので、白・生成り・白・生成りの4つの面に分ける (2026-10-13 オーナー)。面の地は画面の端まで敷く。
   給付金の箱は四角い枠のまま、生成りの面の上に置く。枠の外の地は box-shadow で端まで広げ、clip-path で上下の範囲を切る */
.hp.hp-home #cost{overflow:clip}
.hp.hp-home #cost .pp-pay{position:relative;z-index:0;margin-top:var(--sec);padding:var(--sec) 0}
.hp.hp-home #cost .pp-pay::before{content:"";position:absolute;z-index:-1;top:0;bottom:0;left:-100vw;right:-100vw;background:var(--kinari)}
.hp.hp-home #cost .hx-incl-sum{margin-top:var(--sec)}
.hp.hp-home #cost .hx-grant{margin-top:calc(var(--sec) + 56px);background:#fff;box-shadow:0 0 0 100vmax var(--kinari);clip-path:inset(-56px -100vmax calc(var(--sec) * -1))}
`;

// 押せる範囲を 44px まで広げる。字や枠の見た目は変えず、余白と打ち消しの外側の余白 (行の高さは変わらない) か、透明な疑似要素で当たりだけ足す
const TAP_CSS = `
.hp .hx-tlink,.hp .hx-b-link,.hp .pp-ext{padding-block:11px;margin-block:-11px}
.hp .hx-end-more,.hp summary,.hp li>a:not([class]){padding-block:14px;margin-block:-14px}
.hp .hx-end-more{padding-inline:3px;margin-inline:-3px}
.hp .pp-shot{min-height:44px}
.hp .hx-arrs{gap:12px}
.hp .hx-i-facts dd{text-wrap:balance}
.hp .hx-arr,.hp .pp-youth-cta a{position:relative}
.hp .hx-arr::after,.hp .pp-youth-cta a::after{content:"";position:absolute;left:50%;top:50%;width:max(100%,44px);height:max(100%,44px);transform:translate(-50%,-50%)}
`;

/*
  トップの CSS の束を、トップで足す順に並べる。下層のページはここから要る束だけを選ぶが、並びはこの順のまま
  (順が入れ替わると、同じ強さの指定どうしの勝ち負けが変わる)。どの束をどのページが要るかは、ページの部品が出すクラスで決めた。
  ページに当たらない束を足しても見た目は変わらないので、迷ったら足す。外すと崩れる
*/
const PARTS = {
  legacy: LEGACY_CSS,
  fv: FV_CSS,
  partsSkin: PARTS_SKIN_CSS,
  stickyBar: STICKY_BAR_CSS,
  legacyPc: LEGACY_PC_CSS,
  base: BASE_CSS,
  rowArrows: ROW_ARROWS_CSS,
  aboutVoices: ABOUT_VOICES_CSS,
  hl: HL_CSS,
  plans: PLANS_CSS,
  dt: DT_CSS,
  grant: GRANT_CSS,
  closing: CLOSING_CSS,
  darkFrame: DARK_FRAME_CSS,
  closingFlow: CLOSING_FLOW_CSS,
  phase2a: PHASE2A_CSS,
  phase2b: PHASE2B_CSS,
  fq: FQ_CSS,
  faqEntry: FAQ_ENTRY_CSS,
  vs: VS_CSS,
  rhythm: RHYTHM_CSS,
  motion: MOTION_CSS,
  engineer: ENGINEER_CSS,
  tap: TAP_CSS,
  microMotion: MICRO_MOTION_CSS,
  paper: PAPER_CSS,
  prVideo: PRVIDEO_CSS,
  story: STORY_CSS,
  mrow: MROW_CSS,
};
type Part = keyof typeof PARTS;
// 埋め込むときはコメントと行頭の字下げを落とす (説明はソースに残す)。文字列の中に /* を書いている規則は無い
const pick = (...use: Part[]) =>
  (Object.keys(PARTS) as Part[])
    .filter((k) => use.includes(k))
    .map((k) => PARTS[k])
    .join("")
    .replace(/\/\*[\s\S]*?\*\//g, "")
    .replace(/\n\s+/g, "\n");

// どのページにも要る土台。字と色の変数、節の地、見出し、ボタン、動きの土台、押せる範囲
const COMMON: Part[] = ["legacy", "legacyPc", "base", "rowArrows", "rhythm", "motion", "tap", "mrow"];
// 締めの申し込みの面 (ClosingCta)
const CLOSING: Part[] = ["closing", "darkFrame", "closingFlow"];

/** トップ (/women と広告用の写し /women/lp)。全部の束 */
export const HP_CSS = pick(...(Object.keys(PARTS) as Part[]));
/** 料金のページ。プランの表と比べの表と支払いの例 (sec-vs.ts)、字のマーカー、下に貼り付く帯、締めの面 */
export const HP_PRICING_CSS = pick(...COMMON, ...CLOSING, "stickyBar", "hl", "vs");
/** 受講生の声の一覧と記事。開閉の部品、字のマーカー、下に貼り付く帯、締めの面 */
export const HP_VOICES_CSS = pick(...COMMON, ...CLOSING, "stickyBar", "hl", "dt");
/** 無料30日チャレンジ。墨の節の窓の枠、よくあるご質問の一覧、最初の画面のボタンの矢印の動き */
export const HP_TC_CSS = pick(...COMMON, "darkFrame", "fq", "microMotion");
/** 下層のページ (よくある質問、比較、会社概要、特商法) の末尾の締めの面 (PageEndCta) */
export const HP_END_CSS = pick(...COMMON, ...CLOSING);

// motion-tokens の値を CSS の変数にして .hp に置く
export const HP_MOTION_VARS = Object.fromEntries(
  motionCssVars()
    .split(";")
    .filter(Boolean)
    .map((kv) => [kv.slice(0, kv.indexOf(":")), kv.slice(kv.indexOf(":") + 1)])
) as CSSProperties;
